import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import ZeroDayExpiredIcon from '../../../assets/ZeroDayExpiredIcon';
import ZeroDayIcon from '../../../assets/ZeroDayIcon';
import { MoneyBackBadgeProps } from '../../../types';
import { delay } from '../../../utils/CustomHooks';
import {
  DaysLeftBadgeWrapper,
  OuterBadgeWrapper,
  InnerBadgeWrapper,
  DaysAndTextWrapper,
  DaysContainer,
  DaysTextContainer,
  RingForeground,
  DaysWrapper,
  DayColumn,
  Day,
  RingBackground,
  ExpirationDate,
} from './MoneyBackBadge.styles';

const MoneyBackBadge = ({
  daysLeft,
  isExpired,
  expirationDate,
  isInViewport,
  backgroundColor = '#CFEAF2',
  foregroundColor = '#00AED9',
  customClassName = '',
  customStyles = {},
}: MoneyBackBadgeProps): JSX.Element => {
  const showZeroDayIcon = daysLeft === 0 && !isExpired;
  const showExpiredIcon = daysLeft === 0 && isExpired;

  const [opacity, setOpacity] = useState(0);

  let remainingDays: number = daysLeft;
  let TOTAL_DAYS = 7;

  if (daysLeft < 0) {
    remainingDays = 0;
  } else if (daysLeft > 7) {
    TOTAL_DAYS = daysLeft;
  }
  const [animationDuration, setAnimationDuration] = useState(0);

  const listOfDays = Array.from(Array(TOTAL_DAYS + 1).keys()).reverse();
  const [dayPosition, setDayPosition] = useState(127);

  const daysColumnRef: any = useRef();
  const dayRef: any = useRef();

  const calculatePosition = async () => {
    // Delay 500ms before initializing animation
    await delay(500);
    if (isInViewport) {
      const daysTotalHeight = daysColumnRef.current.offsetHeight;
      const dayHeight = dayRef.current.offsetHeight;
      const dayPositionValue = dayHeight * (remainingDays + 1) - daysTotalHeight;
      setDayPosition(dayPositionValue);
      setAnimationDuration(Math.sqrt((daysTotalHeight - dayHeight * remainingDays) / dayHeight) / 2);

      if (!(TOTAL_DAYS - remainingDays)) {
        setOpacity(1);
      }
    }
  };

  // Day Scroll Transition End handler
  const animationEndHandler = () => {
    setOpacity(1);
  };

  useEffect(() => {
    if (daysColumnRef.current) daysColumnRef.current.addEventListener('transitionend', animationEndHandler);

    return () => {
      if (daysColumnRef.current) {
        daysColumnRef.current.removeEventListener('transitionend', animationEndHandler);
      }
    };
  }, [remainingDays]);

  useLayoutEffect(() => {
    if (!showZeroDayIcon && !showExpiredIcon) {
      calculatePosition();
    }
  }, [remainingDays]);

  const DaysList = (
    <DayColumn duration={animationDuration} style={{ top: `${dayPosition}px` }} ref={daysColumnRef}>
      {listOfDays.map((day: number) => (
        <Day key={day} ref={dayRef}>
          {day}
        </Day>
      ))}
    </DayColumn>
  );

  if (showZeroDayIcon) {
    return (
      <DaysLeftBadgeWrapper>
        <ZeroDayIcon />
      </DaysLeftBadgeWrapper>
    );
  }

  if (showExpiredIcon) {
    return (
      <DaysLeftBadgeWrapper>
        <ZeroDayExpiredIcon />
        <ExpirationDate>{expirationDate}</ExpirationDate>
      </DaysLeftBadgeWrapper>
    );
  }

  return (
    <DaysLeftBadgeWrapper className={customClassName} style={customStyles}>
      <OuterBadgeWrapper style={{ backgroundColor }}>
        <InnerBadgeWrapper style={{ backgroundColor: foregroundColor }}>
          <DaysAndTextWrapper>
            <DaysContainer>
              <RingForeground>
                <svg width="57" height="12" viewBox="0 0 57 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M55.8939 5.35055C53.8237 3.23481 50.5551 2.32807 45.7611 1.42132C39.9865 0.413829 34.2119 0.0108316 28.5462 0.0108316C22.0089 -0.0899178 15.5806 0.514578 9.37012 1.72357C7.19103 2.12657 5.12088 2.83181 3.26865 3.73856C2.50596 4.14155 1.74328 4.6453 1.1985 5.35055C0.435819 6.15654 0 7.16403 0 8.17153C0 9.17902 0.435819 10.1865 1.1985 10.9925L1.30746 11.0933C1.63432 11.3955 2.07014 11.6978 2.39701 12C2.28805 11.7985 2.28805 11.4963 2.28805 11.2948C2.28805 10.1865 2.94178 7.46628 11.7671 5.85429C17.2149 4.94755 22.8805 4.4438 28.4372 4.54455C34.6477 4.4438 40.8581 5.0483 46.8506 6.15654C53.4969 7.56703 54.5864 10.1865 54.5864 11.194C54.5864 11.4963 54.5864 11.6978 54.4775 12C54.9133 11.6978 55.3491 11.3955 55.676 10.9925L55.7849 10.8918C56.5476 10.0858 56.9834 9.07827 56.9834 8.07078C57.0924 7.06328 56.6566 6.05579 55.8939 5.35055Z"
                    fill="#FEB948"
                  />
                </svg>
              </RingForeground>
              <DaysWrapper>{DaysList}</DaysWrapper>
              <RingBackground>
                <svg width="51" height="8" viewBox="0 0 51 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M43.4602 6.36145C45.2655 5.9759 47.0708 5.49398 48.7699 4.81928C49.0885 4.62651 50.6814 4.14458 50.8938 3.85542C51 3.46988 51 3.27711 51 2.89157C50.8938 2.40964 50.6814 1.92771 50.469 1.54217C50.2566 1.15663 49.5133 0.481928 49.0885 0.289157C47.9204 1.15663 44.6283 1.92771 42.823 2.31325H42.7168C37.4071 3.27711 31.9911 3.66265 26.469 3.66265H24.7699C19.354 3.66265 13.8319 3.18072 8.52212 2.21687C7.99115 2.12048 7.35398 2.0241 6.82301 1.83133C5.54867 1.54217 3.84956 0.963856 2.25664 0C1.83186 0.289157 0.876106 0.86747 0.663717 1.25301C0.451328 1.63855 0.0265485 2.21687 0.0265485 2.60241C0.0265485 2.89157 -0.0796458 3.37349 0.132744 3.66265C0.345133 3.95181 2.36283 4.62651 2.68142 4.81928C5.65487 5.78313 7.35398 6.26506 9.69027 6.6506C12.8761 7.22892 16.1681 7.61446 19.354 7.80723C21.3717 7.90362 23.4956 8 25.6195 8C27.6372 8 29.5487 7.90362 31.4602 7.80723C35.4956 7.71084 39.4248 7.13253 43.4602 6.36145Z"
                    fill="#BB8300"
                  />
                </svg>
              </RingBackground>
            </DaysContainer>
            <DaysTextContainer duration={animationDuration} style={{ opacity }}>
              {remainingDays === 1 ? 'Day left' : 'Days left'}
            </DaysTextContainer>
          </DaysAndTextWrapper>
        </InnerBadgeWrapper>
      </OuterBadgeWrapper>
    </DaysLeftBadgeWrapper>
  );
};

export default MoneyBackBadge;
